import { useEffect, useState, useCallback } from "react";
import { api } from "@/api/client";
import { useSession } from "@/context/SessionContext";
import {
  Activity, Cpu, Database, Boxes, Crown, KeyRound, Zap, Wifi, WifiOff, ShieldAlert,
} from "lucide-react";

const Panel = ({ title, icon: Icon, children, testid, className = "" }) => (
  <section
    data-testid={testid}
    className={`bg-white border-4 border-black shadow-[8px_8px_0px_0px_#000] flex flex-col overflow-hidden ${className}`}
  >
    <header className="flex items-center gap-2 px-4 py-3 border-b-4 border-black bg-[#F2C94C] shrink-0">
      <Icon size={18} strokeWidth={2.6} className="text-black" />
      <h2 className="font-mono text-xs font-black uppercase tracking-[0.18em] text-black">{title}</h2>
    </header>
    <div className="flex-1 min-h-0 overflow-y-auto p-4">{children}</div>
  </section>
);

const Stat = ({ label, value, sub, testid }) => (
  <div data-testid={testid} className="border-2 border-black bg-[#FDFBF7] px-3 py-2">
    <p className="font-mono text-[9px] uppercase tracking-widest text-black/50">{label}</p>
    <p className="font-mono text-xl font-black text-black leading-tight">{value}</p>
    {sub && <p className="font-mono text-[9px] text-black/40 mt-0.5">{sub}</p>}
  </div>
);

const Bar = ({ pct }) => (
  <div className="h-3 w-full border-2 border-black bg-white mt-2">
    <div className="h-full bg-black" style={{ width: `${Math.min(100, Math.max(2, pct))}%` }} />
  </div>
);

const STATUS_COLOR = {
  completed: "bg-green-500", processing: "bg-[#F2C94C]", failed: "bg-red-500",
};

// Founder-only live Overload Watchdog gauge — polls the in-memory budget snapshot
// (system ops today vs the 80% halt ceiling + per-user hourly cap).
function WatchdogGauge({ isFounder }) {
  const [wd, setWd] = useState(null);
  useEffect(() => {
    if (!isFounder) return;
    let timer = null;
    const load = () => api.get("/admin/watchdog").then((r) => setWd(r.data)).catch(() => {});
    const start = () => { if (!timer) { load(); timer = setInterval(load, 5000); } };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    // Page Visibility API — pause the 5s poll whenever the tab is hidden/minimised
    // to preserve backend budget, resume (with an immediate refresh) when visible.
    const onVis = () => (document.visibilityState === "hidden" ? stop() : start());
    if (document.visibilityState !== "hidden") start();
    document.addEventListener("visibilitychange", onVis);
    return () => { stop(); document.removeEventListener("visibilitychange", onVis); };
  }, [isFounder]);
  if (!isFounder) return null;
  const pct = wd?.halt_at ? Math.round((wd.system_ops / wd.halt_at) * 100) : 0;
  const danger = pct >= 80;
  return (
    <div data-testid="watchdog-gauge" className="mt-3 border-2 border-black bg-[#FDFBF7] p-3">
      <div className="flex items-center gap-2 mb-2">
        <ShieldAlert size={14} strokeWidth={2.6} className={danger ? "text-red-600" : "text-black"} />
        <p className="font-mono text-[10px] font-black uppercase tracking-widest">Overload Watchdog</p>
        <span data-testid="watchdog-pct" className={`ml-auto font-mono text-sm font-black tabular-nums ${danger ? "text-red-600" : "text-black"}`}>{wd ? `${pct}%` : "…"}</span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <Stat testid="wd-ops" label="Ops Today" value={wd?.system_ops ?? "–"} sub={`ceiling ${wd?.daily_ceiling ?? "–"}`} />
        <Stat testid="wd-users" label="Tracked Users" value={wd?.tracked_users ?? "–"} sub={`hr cap ${wd?.user_hourly_limit ?? "–"}`} />
      </div>
      <Bar pct={pct} />
      <p className={`font-mono text-[9px] mt-1 ${danger ? "text-red-600" : "text-black/40"}`}>
        {wd ? (danger ? "⚠ APPROACHING HALT — 429 imminent" : `Halt at ${wd.halt_at} ops (80%)`) : "Loading watchdog telemetry…"}
      </p>
    </div>
  );
}

export default function DesktopDashboard() {
  const { user } = useSession();
  const [tele, setTele] = useState(null);
  const [online, setOnline] = useState(true);
  const [clock, setClock] = useState(() => new Date());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/system/telemetry");
      setTele(data);
      setOnline(true);
    } catch (e) {
      setOnline(false);
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    const c = setInterval(() => setClock(new Date()), 1000);
    return () => { clearInterval(t); clearInterval(c); };
  }, [load]);

  const meta = user?.meta || {};
  const db = tele?.db || {};
  const tools = tele?.tools || {};
  const jobs = tele?.pipelines || [];
  const colPct = db.cap ? Math.round(((db.collections || 0) / db.cap) * 100) : 0;
  const toolPct = tools.cap ? Math.round(((tools.count || 0) / tools.cap) * 100) : 0;
  const isFounder = !!user && (
    user.role === "founder"
    || (user.email || "").toLowerCase() === "djohnm1972@gmail.com"
    || (user.handle || "").toLowerCase() === "johnnywalker"
  );

  return (
    <div
      data-testid="desktop-dashboard"
      className="hidden lg:flex lg:flex-col lg:ml-[532px] lg:w-[calc(100%-564px)] lg:h-[100vh] lg:p-8 gap-6 text-black"
    >
      {/* Title / status bar */}
      <div className="flex items-center justify-between bg-black text-[#FDFBF7] border-4 border-black shadow-[8px_8px_0px_0px_rgba(242,201,76,1)] px-6 py-4 shrink-0">
        <div className="flex items-center gap-3">
          <Boxes size={26} strokeWidth={2.6} className="text-[#F2C94C]" />
          <div>
            <h1 className="font-mono text-lg font-black uppercase tracking-[0.2em] leading-none">MiSt · Control Deck</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mt-1">Desktop System Analytics &amp; Command</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <span className="font-mono text-sm tabular-nums text-white/80">{clock.toLocaleTimeString()}</span>
          <span className={`flex items-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-widest px-3 py-1.5 border-2 ${online ? "border-green-400 text-green-400" : "border-red-400 text-red-400"}`}>
            {online ? <Wifi size={13} /> : <WifiOff size={13} />}
            {online ? "Live" : "Offline"}
          </span>
        </div>
      </div>

      {/* Region grid */}
      <div className="grid grid-cols-2 grid-rows-2 gap-6 flex-1 min-h-0">
        {/* A — PLATFORM TELEMETRY HUB */}
        <Panel title="Platform Telemetry Hub" icon={Cpu} testid="panel-telemetry">
          <div className="grid grid-cols-2 gap-3">
            <Stat testid="stat-fastapi" label="FastAPI Service" value={online ? "ONLINE" : "DOWN"} sub={tele?.service || "mist-backend"} />
            <Stat testid="stat-db" label="MongoDB" value={db.status ? db.status.toUpperCase() : "…"} sub="Atlas · MingleHubSocials" />
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-2 mb-1">
              <Boxes size={14} strokeWidth={2.6} />
              <p className="font-mono text-[10px] font-black uppercase tracking-widest">Tool Registry</p>
              <span className="ml-auto font-mono text-sm font-black tabular-nums">{tools.count ?? "–"}/{tools.cap ?? 12}</span>
            </div>
            <Bar pct={toolPct} />
            <div className="flex flex-wrap gap-1.5 mt-3">
              {(tools.names || []).map((n) => (
                <span key={n} className="font-mono text-[9px] uppercase tracking-wider border-2 border-black bg-[#FDFBF7] px-1.5 py-0.5">{n}</span>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-2 mb-1">
              <Database size={14} strokeWidth={2.6} />
              <p className="font-mono text-[10px] font-black uppercase tracking-widest">Data Cluster</p>
              <span className="ml-auto font-mono text-sm font-black tabular-nums">{db.collections ?? "–"}/{db.cap ?? 500}</span>
            </div>
            <Bar pct={colPct} />
            <p className="font-mono text-[9px] text-black/40 mt-1">{colPct}% of collection budget used</p>
          </div>
        </Panel>

        {/* C — FOUNDER COMMAND MONITOR */}
        <Panel title="Founder Command Monitor" icon={Crown} testid="panel-founder">
          {user ? (
            <>
              <div className="flex items-center gap-3 border-2 border-black bg-[#FDFBF7] p-3">
                <div className="w-11 h-11 rounded-full border-2 border-black bg-[#F2C94C] grid place-items-center font-black text-black">
                  {(user.name || user.handle || user.email || "?").slice(0, 1).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <p className="font-mono text-sm font-black truncate">{user.name || user.handle || user.email}</p>
                  <p className="font-mono text-[10px] uppercase tracking-widest text-black/50">{user.role || "user"}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <Stat testid="stat-tier" label="Tier" value={(user.subscription_tier || "tier_1").replace("tier_", "T")} sub={user.subscription_plan || (user.lifetime_access ? "lifetime" : "—")} />
                <Stat testid="stat-mtokens" label="M-Tokens" value={Math.round(meta.mTokens || 0)} sub="reward balance" />
                <Stat testid="stat-credits" label="Earned Credits" value={Math.round(meta.earnedCredits || 0)} sub={`cap ${Math.round(meta.monthlyResetCap || 0)}`} />
                <Stat testid="stat-bought" label="Bought Credits" value={Math.round(meta.boughtCredits || 0)} sub="never expire" />
              </div>
              <div className="flex items-center gap-2 mt-3 border-2 border-black px-3 py-2 bg-[#FDFBF7]">
                <KeyRound size={14} strokeWidth={2.6} />
                <p className="font-mono text-[10px] font-black uppercase tracking-widest">BYOK</p>
                <span className={`ml-auto font-mono text-[10px] font-black uppercase px-2 py-0.5 border-2 border-black ${meta.byokActive ? "bg-green-400" : "bg-neutral-200"}`}>
                  {meta.byokActive ? "Active" : "Site Keys"}
                </span>
              </div>
              <WatchdogGauge isFounder={isFounder} />
            </>
          ) : (
            <p className="font-mono text-xs text-black/50">Sign in on the device to load founder metrics.</p>
          )}
        </Panel>

        {/* B — ACTIVE CREATIVE TERMINAL */}
        <Panel title="Active Creative Terminal" icon={Activity} testid="panel-pipelines" className="col-span-2">
          {jobs.length === 0 ? (
            <div className="h-full grid place-items-center">
              <p className="font-mono text-xs uppercase tracking-widest text-black/40">No recent generation jobs</p>
            </div>
          ) : (
            <div className="space-y-2">
              {jobs.map((j, i) => (
                <div key={i} data-testid="pipeline-row" className="flex items-center gap-3 border-2 border-black bg-[#FDFBF7] px-3 py-2">
                  <span className={`w-3 h-3 rounded-full border-2 border-black ${STATUS_COLOR[j.status] || "bg-neutral-300"} ${j.status === "processing" ? "animate-pulse" : ""}`} />
                  <Zap size={14} strokeWidth={2.6} className="text-black/60" />
                  <span className="font-mono text-xs font-black uppercase tracking-wider">{(j.kind || "job").replace(/_/g, " ")}</span>
                  {j.engine && <span className="font-mono text-[9px] uppercase tracking-wider border border-black px-1.5 py-0.5 text-black/60">{j.engine}</span>}
                  <span className="ml-auto font-mono text-[10px] uppercase tracking-widest text-black/50">{j.status}</span>
                  <span className="font-mono text-[9px] text-black/30 tabular-nums">{(j.created_at || "").slice(11, 19)}</span>
                </div>
              ))}
            </div>
          )}
        </Panel>
      </div>
    </div>
  );
}
